import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { FaSearch, FaReceipt, FaChevronRight } from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, StatusBadge, EmptyState } from '../components/ui'
import PipelineBar from '../components/charts/PipelineBar'
import { money, number, formatDateTime, relativeTime } from '../lib/format'
import * as api from '../lib/api'

const STATUS_TABS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Awaiting payment' },
  { value: 'paid', label: 'Paid' },
  { value: 'processing', label: 'Packing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
]

const OrdersPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const status = searchParams.get('status') ?? 'all'
  const [search, setSearch] = useState('')
  const [orders, setOrders] = useState([])
  const [counts, setCounts] = useState(new Map())
  const [state, setState] = useState({ loading: true, error: null })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setState({ loading: true, error: null })
      try {
        const [rows, statusCounts] = await Promise.all([
          api.getOrders({ status }),
          api.getOrderStatusCounts(),
        ])
        if (cancelled) return
        setOrders(rows ?? [])
        setCounts(statusCounts ?? new Map())
        setState({ loading: false, error: null })
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [status])

  // Searching filters in memory rather than re-querying on every keystroke.
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return orders
    return orders.filter(
      (o) =>
        o.order_number.toLowerCase().includes(query) ||
        `${o.customer_first_name} ${o.customer_last_name}`.toLowerCase().includes(query) ||
        (o.customer_email ?? '').toLowerCase().includes(query),
    )
  }, [orders, search])

  const revenue = useMemo(
    () =>
      visible
        .filter((o) => o.payment_state === 'complete' && o.status !== 'cancelled')
        .reduce((n, o) => n + Number(o.total || 0), 0),
    [visible],
  )

  if (state.loading) return <Spinner label="Loading orders…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load orders.</strong> {state.error.message}</Banner>
  }

  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle={`${number(visible.length)} shown · ${money(revenue)} paid`}
      />

      <div className="mb-4">
        <Card title="Pipeline" subtitle="Click a segment to filter">
          <PipelineBar
            counts={counts}
            onSelect={(next) => setSearchParams(next === status ? {} : { status: next })}
          />
        </Card>
      </div>

      <Card bodyClassName="">
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline p-3">
          <div className="relative min-w-[200px] flex-1">
            <FaSearch
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted"
              aria-hidden="true"
            />
            <input
              className="input pl-8"
              placeholder="Search order number, name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search orders"
            />
          </div>

          <select
            className="input input-sm w-auto"
            value={status}
            onChange={(e) => setSearchParams(e.target.value === 'all' ? {} : { status: e.target.value })}
            aria-label="Filter by status"
          >
            {STATUS_TABS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {visible.length === 0 ? (
          <EmptyState icon={FaReceipt} title="No orders here">
            {status === 'all'
              ? 'Orders placed on the shop will appear here.'
              : 'No orders currently sit at that stage.'}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Placed</th>
                  <th>Customer</th>
                  <th>Deliver to</th>
                  <th>Status</th>
                  <th className="num">Items</th>
                  <th className="num">Total</th>
                  <th aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {visible.map((order) => (
                  // The WHOLE row opens the order, not just the order number.
                  // A 12-character link inside a 1400px-wide row is a target
                  // people miss — the natural instinct is to click the row.
                  //
                  // The order number stays a real <Link> underneath so that
                  // right-click, middle-click and "open in new tab" still behave
                  // like links; onClick handles everything else.
                  <tr
                    key={order.id}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/orders/${order.id}`)
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    aria-label={`Open order ${order.order_number}`}
                    className="group cursor-pointer"
                  >
                    <td>
                      <Link
                        to={`/orders/${order.id}`}
                        // Without this the row handler fires too and navigates
                        // twice, pushing a duplicate history entry that makes
                        // Back appear broken.
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-ink group-hover:text-brand-red"
                      >
                        {order.order_number}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap text-2xs text-ink-secondary">
                      {relativeTime(order.placed_at)}
                      <div className="text-ink-muted">{formatDateTime(order.placed_at)}</div>
                    </td>
                    <td className="max-w-[160px]">
                      <div className="truncate">
                        {order.customer_first_name} {order.customer_last_name}
                      </div>
                      <div className="truncate text-2xs text-ink-muted">{order.customer_email}</div>
                    </td>
                    <td className="max-w-[140px] truncate text-2xs text-ink-secondary">
                      {order.shipping_city}
                      {order.shipping_province ? `, ${order.shipping_province}` : ''}
                    </td>
                    <td><StatusBadge status={order.status} /></td>
                    <td className="num">{order.item_count}</td>
                    <td className="num font-semibold">{money(order.total)}</td>
                    <td className="w-6 text-right">
                      <FaChevronRight
                        className="text-[10px] text-ink-muted/40 transition-colors group-hover:text-brand-red"
                        aria-hidden="true"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

export default OrdersPage
